import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { useTheme } from '@/app/ThemeContext';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import type { OperationsPlugin } from '@/sections/customerManagement/components/masterDashboard/hooks/useAllDiscoveryPlugins';
import type { ProviderCredits } from '@/sections/customerManagement/components/masterDashboard/hooks/usePluginCredits';

export interface PluginStatusCardProps {
  plugin: OperationsPlugin;
  onEdit?: (plugin: OperationsPlugin) => void;
  isPraetorianUser?: boolean;
  onToggleEnabled?: (plugin: OperationsPlugin, enabled: boolean) => void;
  isTogglePending?: boolean;
  credits?: ProviderCredits;
}

export function PluginStatusCard({
  plugin,
  onEdit,
  isPraetorianUser = false,
  onToggleEnabled,
  isTogglePending = false,
  credits,
}: PluginStatusCardProps) {
  const { currentTheme } = useTheme();
  const [iconError, setIconError] = useState(false);
  const isClickable = plugin.needsApiKey && !!onEdit;
  const isGloballyDisabled = plugin.status === 'globally-disabled';
  const showToggle = isPraetorianUser && !!plugin.piusId;

  return (
    <article
      aria-label={`${plugin.name} - ${plugin.status}`}
      className={twMerge(
        'border-chariot-stroke bg-chariot-background-primary flex flex-col rounded-lg border transition-colors',
        isGloballyDisabled && 'border-destructive/30',
        plugin.status === 'disabled' && !isGloballyDisabled && 'opacity-60'
      )}
    >
      {/* Globally disabled banner */}
      {isGloballyDisabled && (
        <div className="bg-destructive/10 text-destructive rounded-t-lg px-4 py-1.5 text-xs font-semibold tracking-wider uppercase">
          All Discovery Disabled
        </div>
      )}

      <div
        className={twMerge(
          'flex flex-col p-4',
          isGloballyDisabled && 'rounded-b-lg'
        )}
      >
        <div className="flex items-start justify-between gap-4">
          <div
            className={twMerge(
              'flex-1',
              isGloballyDisabled && 'text-muted-foreground'
            )}
          >
            <div className="flex items-center gap-2">
              {!iconError && (
                <img
                  src={`/icons/${currentTheme}/${plugin.id}.svg`}
                  alt={plugin.name}
                  className="size-5 rounded"
                  onError={() => setIconError(true)}
                />
              )}
              <h3
                className={twMerge(
                  'text-chariot-text-primary text-sm font-medium',
                  isGloballyDisabled && 'text-muted-foreground'
                )}
              >
                {plugin.name}
              </h3>
              {credits && (
                <span className="text-chariot-text-tertiary text-xs">
                  Credits available: {credits.remaining.toLocaleString()}
                </span>
              )}
            </div>
            <p
              className={twMerge(
                'text-chariot-text-secondary mt-2 text-xs',
                isGloballyDisabled && 'text-muted-foreground'
              )}
            >
              {plugin.description}
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            {plugin.status === 'active' && (
              <span className="text-success flex items-center gap-1 text-xs">
                <CheckCircleIcon className="size-4" />
                Active
              </span>
            )}
            {plugin.status === 'no-key' && (
              <span className="text-chariot-status-warning flex items-center gap-1 text-xs">
                <XCircleIcon className="size-4" />
                No Key
              </span>
            )}
            {plugin.status === 'disabled' && (
              <span className="text-chariot-text-tertiary flex items-center gap-1 text-xs">
                <XCircleIcon className="size-4" />
                Disabled
              </span>
            )}
            {isGloballyDisabled && (
              <span className="text-muted-foreground flex items-center gap-1 text-xs">
                <XCircleIcon className="size-4" />
                Globally Disabled
              </span>
            )}

            {isClickable && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit?.(plugin)}
                aria-label={`Edit ${plugin.name} configuration`}
              >
                Edit
              </Button>
            )}
          </div>
        </div>

        {/* Per-plugin toggle (Praetorian users only, paid plugins only) */}
        {showToggle && (
          <>
            <Separator className="mt-3 mb-3" />
            <div className="flex items-center justify-between">
              <span
                className={twMerge(
                  'text-chariot-text-secondary text-xs',
                  isGloballyDisabled && 'text-muted-foreground'
                )}
              >
                Enabled
              </span>
              <Switch
                checked={plugin.enabled}
                onCheckedChange={enabled => onToggleEnabled?.(plugin, enabled)}
                disabled={isTogglePending || isGloballyDisabled}
                aria-label={`${plugin.enabled ? 'Disable' : 'Enable'} ${plugin.name} for all accounts`}
              />
            </div>
          </>
        )}
      </div>
    </article>
  );
}
